import { List } from 'immutable'
import { Moment } from 'moment'
import React from 'react'
import { Action, Fun, IOWidget, lifecycle, mk_widget, mkWidget, nothing } from 'widgets-for-react'
import { video, VideoProps } from '../../video'
import { Breadcrumb } from '../../wordpress/types_and_parser/extra_types.mod'
import { ExtraImages } from '../utils/extra_images.mod'
import { get_viewport_size } from '../utils/get_viewport_size'

type HomepageHeaderProps = {
  kind: 'homepage'
  title: string
}

type GeneralPageHeaderProps<extra_images> = {
  kind: 'page'
} & MediaHeaderProps<extra_images>

type NewsPageHeaderProps<extra_images> = {
  kind: 'news'
  created: Moment
  author: Author
} & MediaHeaderProps<extra_images>

export type Author = {
  job: string
  name: string
  image_alt: string
  image_src: string
}

type MediaHeaderProps<extra_images> = {
  title: string
  subtitle?: string
  intro?: string
  kind_media: 'video' | 'image' | 'none'
  image_url?: string
  image_alt_text?: string
  video?: VideoProps
}

export type PageHeaderProps<extra_images> =
  | { kind: 'empty' }
  | HomepageHeaderProps
  | GeneralPageHeaderProps<extra_images>
  | NewsPageHeaderProps<extra_images>

export const pageHeader: Fun<
  List<Breadcrumb>,
  IOWidget<PageHeaderProps<ExtraImages>, PageHeaderProps<ExtraImages>>
> = breadcrumbs => props =>
  props.kind == 'homepage'
    ? homepageHeader(breadcrumbs)(props)
    : props.kind == 'page' || props.kind == 'news'
    ? genericMediaHeader(breadcrumbs)(props)
    : nothing()

export const homepageHeader: Fun<
  List<Breadcrumb>,
  IOWidget<HomepageHeaderProps, HomepageHeaderProps>
> = breadcrumbs => props =>
  lifecycle<HomepageHeaderProps>({
    componentDidMount: () => {
      // from: https://github.com/hoppinger/rotterdam-festivals/blob/c1b7c932a9383998e319b60fa532ea970306b349/app/assets/javascripts/custom/helpers.js

      const video = document.querySelector('[data-js-header-video]')

      if (!video) return

      let video_src = video.getAttribute('data-src-sd-s') // Smaller than 40em (480px)

      if (typeof video.getAttribute('data-src-sd-l') !== 'undefined' && get_viewport_size().width / 16 >= 30) {
        // Larger than 40em (480px)
        video_src = video.getAttribute('data-src-sd-l')
      }

      if (typeof video.getAttribute('data-src-hd-s') !== 'undefined' && get_viewport_size().width / 16 >= 60) {
        // Larger than 60em (960px)
        video_src = video.getAttribute('data-src-hd-s')
      }

      if (typeof video.getAttribute('data-src-hd-l') !== 'undefined' && get_viewport_size().width / 16 >= 90) {
        // Larger than 80em (1440px)
        video_src = video.getAttribute('data-src-hd-l')
      }

      video.setAttribute('src', video_src)
    }
  })(props =>
    mkWidget<Action<HomepageHeaderProps>>({
      run: () => (
        <header className="hero-home">
          <div className="hero-home__video-wrap">
            <video
              data-js-header-video
              className="hero-home__video"
              preload="metadata"
              data-src-sd-s="https://player.vimeo.com/progressive_redirect/playback/1050710931/rendition/360p/file.mp4?loc=external&log_user=0&signature=28eb2c0cb5e9b95a3ec859451931aba099d52b060e29b9cc4781fc46758abde4" // 640
              data-src-sd-l="https://player.vimeo.com/progressive_redirect/playback/1050710931/rendition/540p/file.mp4?loc=external&log_user=0&signature=356dbd0f5b0e175c1ff3a802c666fa82d26b1e99111e0b0d03c2ab1174a7349b" // 960
              data-src-hd-s="https://player.vimeo.com/progressive_redirect/playback/1050710931/rendition/720p/file.mp4?loc=external&log_user=0&signature=ffdac7c5734d30eaa24ba0bd4ba1b0f2385710272c16d75cc030c17c450b0fd1" // 1280
              data-src-hd-l="https://player.vimeo.com/progressive_redirect/playback/1050710931/rendition/1080p/file.mp4?loc=external&log_user=0&signature=858e474874ae9e9e2bac401cc95f59c06cfb0019dbb6addd82513462763df119" // 1920
              src="https://player.vimeo.com/progressive_redirect/playback/1050710931/rendition/360p/file.mp4?loc=external&log_user=0&signature=28eb2c0cb5e9b95a3ec859451931aba099d52b060e29b9cc4781fc46758abde4" // 640
              loop={true}
              muted={true}
              playsInline={true}
              autoPlay={true}
            />
          </div>
          <h1 className="title--xl hero-home__title">{props.title}</h1>
        </header>
      )
    })
  )(props).map(a => a(props))

export const genericMediaHeader: Fun<
  List<Breadcrumb>,
  IOWidget<GeneralPageHeaderProps<ExtraImages> | NewsPageHeaderProps<ExtraImages>, never>
> = breadcrumbs => props =>
  mk_widget({
    run: o => (
      <header
        className={`hero ${props.kind_media === 'image' || props.kind_media === 'video' ? 'hero--with-media' : null}`}
      >
        <div className="block">
          <div className="hero__inner">
            {breadcbumbs(breadcrumbs).run(() => null)}

            <h1 className="title--xl hero__title">{props.title}</h1>

            {props.subtitle && props.kind == 'news' ? (
              <div className="hero__meta">
                <div>
                  <svg aria-hidden="true" width="13" height="13">
                    <use xlinkHref="#icon--clock" />
                  </svg>
                  <time dateTime={props.created.toISOString().split('T')[0]}>{props.created.format('LL')}</time>
                </div>
                <div>
                  <svg aria-hidden="true" width="13" height="13">
                    <use xlinkHref="#icon--person" />
                  </svg>
                  <span>{props.author.name}</span>
                </div>
              </div>
            ) : props.subtitle ? (
              <p className="title--m title--thin hero__subtitle">{props.subtitle}</p>
            ) : (
              <></>
            )}

            {props.intro && <div className="hero__description" dangerouslySetInnerHTML={{ __html: props.intro }} />}

            {props.kind_media != 'none' && (
              <div className="hero__media">
                {props.kind_media == 'video' ? (
                  video('image_url' in props ? props.image_url : '')({ ...props.video, height: 540, width: 960 }).run(
                    () => null
                  )
                ) : (
                  <img src={props.image_url} alt={props.image_alt_text || ''} width="512" height="342" loading="eager" />
                )}
              </div>
            )}
          </div>
        </div>
      </header>
    )
  })

export const breadcbumbs: IOWidget<List<Breadcrumb>, never> = breadcrumbs =>
  mk_widget({
    run: () =>
      !breadcrumbs.isEmpty() ? (
        <nav aria-label="Broodkruimels" className="breadcrumbs">
          <ol typeof="BreadcrumbList" vocab="http://schema.org/">
            <li property="itemListElement" typeof="ListItem" className="breadcrumb">
              <a property="item" typeof="WebPage" href="/" className="link link--with-icon">
                <span property="name">Home</span>
                <svg className="breadcrumb__icon" width="6" height="8" aria-hidden="true">
                  <use xlinkHref="#icon--caret" />
                </svg>
                <meta property="position" content="1" />
              </a>
            </li>
            {breadcrumbs.map((breadcrumb, index) => (
              <>
                {/* Last item in breadcrumb shouldn't be clickable so check if the index+1 is the same length as the one of the breadcrumbs */}
                {breadcrumbs.size === index + 1 ? (
                  <li
                    key={index}
                    property="itemListElement"
                    typeof="ListItem"
                    className="breadcrumb"
                    aria-current="page"
                  >
                    <span property="name">{breadcrumb.title}</span>
                    <meta property="position" content={`${index + 2}`} />
                  </li>
                ) : (
                  <li key={index} property="itemListElement" typeof="ListItem" className="breadcrumb">
                    <a property="item" typeof="WebPage" href={breadcrumb.url} className="link link--with-icon">
                      <span property="name">{breadcrumb.title}</span>
                      <svg className="breadcrumb__icon" width="6" height="8" aria-hidden="true">
                        <use xlinkHref="#icon--caret" />
                      </svg>
                      <meta property="position" content={`${index + 2}`} />
                    </a>
                  </li>
                )}
              </>
            ))}
          </ol>
        </nav>
      ) : null
  })